/**
 * Auth Sync Service
 *
 * Handles cross-tab synchronization using BroadcastChannel API.
 * When user logs out in one tab, all other tabs are notified.
 *
 * @module utils/authSync
 */

import { invalidateRouter } from '../router/routerInvalidation';
import { CHANNEL_NAMES } from '../sessionConfig';
import { sessionLog } from './sessionLog';

const CHANNEL_NAME = CHANNEL_NAMES.AUTH_SYNC;

/** @type {BroadcastChannel | null} */
let channel = null;

/** @typedef {'LOGOUT' | 'LOGIN' | 'PERMISSIONS_CHANGED'} AuthMessageType */

/**
 * Initialize the auth sync channel.
 * Call once on app bootstrap.
 */
export function initAuthSync() {
  if (channel) return;

  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = handleAuthMessage;
    sessionLog('[BB:AuthSync] BroadcastChannel initialized');
  } catch (e) {
    // BroadcastChannel not supported (very old browsers)
    console.warn('[BB:AuthSync] BroadcastChannel not supported:', e);
  }
}

/**
 * Handle incoming auth messages from other tabs.
 *
 * @param {MessageEvent} event
 */
function handleAuthMessage(event) {
  const { type, reason } = event.data;
  sessionLog('[BB:AuthSync] Received message from other tab:', { type, reason });

  switch (type) {
    case 'LOGOUT': {
      sessionLog('[BB:AuthSync] Logout received from another tab:', reason);
      // CRITICAL: Use hard reload to purge all memory state
      // Do NOT use router.navigate - keeps JS heap with sensitive data
      // Preserve current path as redirect so user returns here after re-login.
      // Strip any existing 'redirect' param to prevent nested redirect chains.
      // Skip saving redirect if already on the login page to avoid login→login loops.
      const url = new URL(window.location.href);
      url.searchParams.delete('redirect');
      const returnUrl = url.pathname + url.search;
      const isAlreadyOnLogin = url.pathname.endsWith('/login');
      const target = isAlreadyOnLogin
        ? '/bbweb/login'
        : `/bbweb/login?redirect=${encodeURIComponent(returnUrl)}`;
      window.location.href = target;
      break;
    }

    case 'PERMISSIONS_CHANGED':
      sessionLog(
        '[BB:AuthSync] Permissions changed in another tab, invalidating router'
      );
      // Trigger router re-evaluation
      invalidateRouter();
      break;

    default:
      break;
  }
}

/**
 * Broadcast logout to all other tabs.
 *
 * @param {'user_action' | 'session_expired' | 'token_invalid'} [reason='user_action']
 */
export function broadcastLogout(reason = 'user_action') {
  if (channel) {
    sessionLog('[BB:AuthSync] Broadcasting logout to other tabs:', reason);
    channel.postMessage({ type: 'LOGOUT', reason });
  }
}

/**
 * Broadcast permission change to all tabs.
 */
export function broadcastPermissionChange() {
  if (channel) {
    sessionLog('[BB:AuthSync] Broadcasting permission change to other tabs');
    channel.postMessage({ type: 'PERMISSIONS_CHANGED' });
  }
}

/**
 * Cleanup on app unmount (rarely needed for SPAs).
 */
export function destroyAuthSync() {
  if (channel) {
    channel.close();
    channel = null;
  }
}
